import { useCallback, useEffect, useState } from 'react'
import { useEffectOnce } from 'react-use'

declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void
      execute: (sitekey: string, params: { action: string }) => Promise<string>
      // ...other methods and properties
    }
  }
}

const showBadge = () => {
  if (!window?.grecaptcha) {
    return
  }
  window?.grecaptcha?.ready?.(() => {
    const badge = document.getElementsByClassName(
      'grecaptcha-badge',
    )[0] as HTMLElement
    if (!badge) {
      return
    }
    // badge.style.display = 'block'
    // badge.style.zIndex = '1'
    badge.style.visibility = 'hidden'
  })
}

const hideBadge = () => {
  if (!window?.grecaptcha) {
    return
  }
  window?.grecaptcha?.ready?.(() => {
    const badge = document.getElementsByClassName(
      'grecaptcha-badge',
    )[0] as HTMLElement
    if (!badge) {
      return
    }
    badge.style.display = 'none'
  })
}

export const isProd = process.env.NODE_ENV === 'production'

const useReCaptcha = ({
  key,
  delay = 1000,
}: {
  key?: string
  delay?: number
}): {
  reCaptchaLoaded: boolean
  generateReCaptchaToken: (action: string) => Promise<string>
} => {
  const [reCaptchaLoaded, setReCaptchaLoaded] = useState(!isProd)

  const load = useCallback(() => {
    if (typeof window === 'undefined' || key == null) {
      return
    }

    if (window.grecaptcha || document.getElementById('grecaptcha')) {
      setReCaptchaLoaded(true) // already loaded
      return
    }

    console.info('Attempting to load reCAPTCHA')
    const script = document.createElement('script')
    script.id = 'grecaptcha'
    script.src = `https://www.google.com/recaptcha/api.js?render=${key}`
    script.async = true
    script.onload = () => {
      console.info('reCAPTCHA loaded.')
      setReCaptchaLoaded(true)
      showBadge()
    }

    document.body.appendChild(script)
  }, [key])

  useEffectOnce(() => {
    if (delay) {
      setTimeout(load, delay)
    } else {
      load()
    }
  })

  // Hide badge when unmount
  useEffect(() => {
    return hideBadge // Return the function for cleanup to call on component unmount
  }, [])

  // Get token
  const generateReCaptchaToken = (action: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!isProd) {
        resolve('development')
      } else if (!reCaptchaLoaded) {
        return reject(new Error('ReCaptcha not loaded'))
      } else if (
        typeof window === 'undefined' ||
        key == null ||
        !window.grecaptcha
      ) {
        setReCaptchaLoaded(false)
        return reject(new Error('ReCaptcha not loaded'))
      } else {
        window.grecaptcha.ready(() => {
          window.grecaptcha.execute(key, { action }).then((token: string) => {
            localStorage.setItem('recaptcha_token', token)
            resolve(token)
          })
        })
      }
    })
  }

  return {
    reCaptchaLoaded: reCaptchaLoaded,
    generateReCaptchaToken,
  }
}

export default useReCaptcha
